/**
 * CenikFields — the pricing part of the tarif form (P5-b): the markup, the rental-length bands and the seasons. A pure controlled block
 * (the tarif form owns the state); the rules are checked by the server. "Načíst vzor" fills the fields with the legacy configuration of
 * one of the five original tarify — nothing is saved until the form is.
 */

import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Field } from '@/components/Field';
import { Menu } from '@/components/Menu';
import { TextInput } from '@/components/TextInput';
import { cenikJePrazdny, cenikSeed, MAX_DELEK, MAX_SEZON, type CenikValue, type DelkaRadek, type SezonaRadek } from './cenik-helpers';
import { CENIK_VZORY } from './cenik-vzory';

interface CenikFieldsProps {
  value: CenikValue;
  onChange: (next: CenikValue) => void;
  busy?: boolean;
}

export function CenikFields({ value, onChange, busy = false }: CenikFieldsProps) {
  const { confirm, dialog } = useConfirm();
  const set = (patch: Partial<CenikValue>) => onChange({ ...value, ...patch });
  const zmenDelku = (i: number, patch: Partial<DelkaRadek>) => set({ delky: value.delky.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const zmenSezonu = (i: number, patch: Partial<SezonaRadek>) => set({ sezony: value.sezony.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  // a vzor replaces the whole block, so what was typed is not thrown away without asking
  const nactiVzor = async (vzor: (typeof CENIK_VZORY)[number]) => {
    if (!cenikJePrazdny(value) && !(await confirm({ title: 'Nahradit zadaný ceník?', description: `Zadané hodnoty ceníku se nahradí vzorem „${vzor.nazev}“. Změna se uloží až uložením tarifu.`, confirmLabel: 'Nahradit', tone: 'warn' }))) return;
    onChange(cenikSeed(vzor));
  };

  return (
    <section data-testid="cenik" className="mt-8 flex flex-col gap-6 border-t border-line pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Ceník</div>
          <p className="mt-1 text-[12px] text-ink-3">
            Bez marže nelze vozidla tohoto tarifu ocenit. Cena za den = režijní cena + marže, dál se násobí pásmem délky a sezónou.
          </p>
        </div>
        <Menu>
          <Menu.Trigger asChild>
            <Button variant="secondary" size="sm" disabled={busy} data-testid="cenik-vzor">
              Načíst vzor
            </Button>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Vzor z původního systému</Menu.Label>
            {CENIK_VZORY.map((v) => (
              <Menu.Item key={v.nazev} onSelect={() => void nactiVzor(v)}>
                {v.nazev}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu>
      </div>

      <div className="max-w-[16rem]">
        <Field label="Marže nad režijní cenu" optional="volitelné" hint="V %, např. 80.">
          <TextInput
            inputMode="decimal"
            value={value.marze}
            suffix="%"
            disabled={busy}
            data-testid="cenik-marze"
            onChange={(e) => set({ marze: e.currentTarget.value })}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-3" data-testid="cenik-delky">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Pásma délky pronájmu · {value.delky.length}</div>
        <p className="text-[12px] text-ink-3">Od kolika dní se denní cena násobí koeficientem (první pásmo od 1 dne, nejvýše {MAX_DELEK}).</p>
        {value.delky.map((r, i) => (
          <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-3">
            <Field label="Od dne">
              <TextInput inputMode="numeric" value={r.od} disabled={busy} data-testid={`cenik-delka-${i}-od`} onChange={(e) => zmenDelku(i, { od: e.currentTarget.value })} />
            </Field>
            <Field label="Koeficient">
              <TextInput inputMode="decimal" value={r.koef} disabled={busy} data-testid={`cenik-delka-${i}-koef`} onChange={(e) => zmenDelku(i, { koef: e.currentTarget.value })} />
            </Field>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => set({ delky: value.delky.filter((_, j) => j !== i) })}>
              Odebrat
            </Button>
          </div>
        ))}
        {value.delky.length < MAX_DELEK && (
          <div>
            <Button variant="secondary" size="sm" disabled={busy} data-testid="cenik-delka-pridat" onClick={() => set({ delky: [...value.delky, { od: value.delky.length === 0 ? '1' : '', koef: '' }] })}>
              Přidat pásmo
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3" data-testid="cenik-sezony">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Sezóny · {value.sezony.length}</div>
        <p className="text-[12px] text-ink-3">
          Sezóna platí od data vyzvednutí (MM-DD, každý rok stejně) do dne před další sezónou; první od 01-01, nejvýše {MAX_SEZON}. Paušál: do N dní je celé nájemné
          pevná cena.
        </p>
        {value.sezony.map((r, i) => (
          <div key={i} className="grid grid-cols-2 items-end gap-3 border border-line p-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
            <Field label="Od (MM-DD)">
              <TextInput value={r.od} placeholder="01-01" maxLength={5} disabled={busy} data-testid={`cenik-sezona-${i}-od`} onChange={(e) => zmenSezonu(i, { od: e.currentTarget.value })} />
            </Field>
            <Field label="Koeficient">
              <TextInput inputMode="decimal" value={r.koef} disabled={busy} data-testid={`cenik-sezona-${i}-koef`} onChange={(e) => zmenSezonu(i, { koef: e.currentTarget.value })} />
            </Field>
            <Field label="Paušál do dní" optional="volitelné">
              <TextInput inputMode="numeric" value={r.pausalDny} disabled={busy} data-testid={`cenik-sezona-${i}-pausal-dny`} onChange={(e) => zmenSezonu(i, { pausalDny: e.currentTarget.value })} />
            </Field>
            <Field label="Paušál cena" optional="volitelné">
              <TextInput inputMode="numeric" value={r.pausalCena} suffix="Kč" disabled={busy} data-testid={`cenik-sezona-${i}-pausal-cena`} onChange={(e) => zmenSezonu(i, { pausalCena: e.currentTarget.value })} />
            </Field>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => set({ sezony: value.sezony.filter((_, j) => j !== i) })}>
              Odebrat
            </Button>
          </div>
        ))}
        {value.sezony.length < MAX_SEZON && (
          <div>
            <Button variant="secondary" size="sm" disabled={busy} data-testid="cenik-sezona-pridat" onClick={() => set({ sezony: [...value.sezony, { od: value.sezony.length === 0 ? '01-01' : '', koef: '', pausalDny: '', pausalCena: '' }] })}>
              Přidat sezónu
            </Button>
          </div>
        )}
      </div>
      {dialog}
    </section>
  );
}
